import React, { useEffect, useState } from 'react';
import { Table, Card, Row, Col } from 'antd';
import MyPieChart from '../../components/myPieChart/MyPieChart';
import MyBarChart from '../myBarChart/MyBarChart';
import './Panel.scss';
import { reportRequests } from '../../requests';
import { toCurrency, getFormattedDouble } from '../../utils/format';

// Dados de Receitas por Categoria
const getRevenueData = (item) => [
  {
    key: 'disparos',
    category: 'Disparos',
    revenueAchieved: item.sends,
    revenueProjection: item.sendsProjection,
    requiredForTarget: item.sendsProjection ? item.sendsProjection - item.sends : null,
  },
  {
    key: 'tyra',
    category: 'Tyra',
    revenueAchieved: item.tyra,
    revenueProjection: item.tyraProjection,
    requiredForTarget: item.tyraProjection ? item.tyraProjection - item.tyra : null,
  },
  {
    key: 'marketstories',
    category: 'Marketstories',
    revenueAchieved: item.marketStories,
    revenueProjection: item.marketStoriesProjection,
    requiredForTarget: item.marketStoriesProjection ? item.marketStoriesProjection - item.marketStories : null,
  },
];

const revenueColumns = [
  {
    title: 'Categoria',
    dataIndex: 'category',
    key: 'category',
  },
  {
    title: 'Receita Realizada',
    dataIndex: 'revenueAchieved',
    key: 'revenueAchieved',
    render: (value) => toCurrency(value),
  },
  {
    title: 'Receita Projetada',
    dataIndex: 'revenueProjection',
    key: 'revenueProjection',
    render: (value) => toCurrency(value),
  },
  {
    title: 'Necessário para a Meta',
    dataIndex: 'requiredForTarget',
    key: 'requiredForTarget',
    render: (value) => value > 0 ? toCurrency(value) : 'Atingida',
  },
];

// Dados do Painel de Metas e Lucro
const panelData = [
  {
    key: 'row1',
    despesa: 'Impostos',
    despesaValor: 'R$ 5.000,00',
    receitaAtualCategoria: 'Disparos',
    receitaAtualValor: 'R$ 8.200,00',
    receitaProjCategoria: 'Disparos',
    receitaProjValor: 'R$ 10.000,00',
  },
  {
    key: 'row2',
    despesa: 'Custos Totais',
    despesaValor: 'R$ 10.000,00',
    receitaAtualCategoria: 'Tyra',
    receitaAtualValor: 'R$ 28.000,00',
    receitaProjCategoria: 'Tyra',
    receitaProjValor: 'R$ 30.000,00',
  },
  {
    key: 'row3',
    despesa: 'Pessoas',
    despesaValor: 'R$ 8.000,00',
    receitaAtualCategoria: 'Marketstories',
    receitaAtualValor: 'R$ 3.000,00',
    receitaProjCategoria: 'Marketstories',
    receitaProjValor: 'R$ 5.000,00',
  },
  // Linha de Totais
  {
    key: 'total',
    despesa: 'Total',
    despesaValor: 'R$ 23.000,00',
    receitaAtualCategoria: 'Total',
    receitaAtualValor: 'R$ 39.200,00',
    receitaProjCategoria: 'Total',
    receitaProjValor: 'R$ 45.000,00',
  },
];

const panelColumns = [
  {
    title: 'Despesas',
    children: [
      {
        title: 'Categoria',
        dataIndex: 'despesa',
        key: 'despesa',
      },
      {
        title: 'Valor',
        dataIndex: 'despesaValor',
        key: 'despesaValor',
      },
    ],
  },
  {
    title: 'Receitas atuais',
    children: [
      {
        title: 'Categoria',
        dataIndex: 'receitaAtualCategoria',
        key: 'receitaAtualCategoria',
      },
      {
        title: 'Valor',
        dataIndex: 'receitaAtualValor',
        key: 'receitaAtualValor',
      },
    ],
  },
  {
    title: 'Receitas projetadas',
    children: [
      {
        title: 'Categoria',
        dataIndex: 'receitaProjCategoria',
        key: 'receitaProjCategoria',
      },
      {
        title: 'Valor',
        dataIndex: 'receitaProjValor',
        key: 'receitaProjValor',
      },
    ],
  },
];

const getDataSend = (item) => {
  let achieved = item.sends * 100 / item.sendsProjection;
  achieved = achieved > 100 ? 100 : parseFloat(achieved.toFixed(2));
  const toGoal = parseFloat((100 - achieved).toFixed(2));
  return [
    { name: 'Receita realizada', value: achieved, color: '#4CAF50' },
    { name: 'Necessário para a meta', value: toGoal, color: '#F44336' },
  ];
}

const getDataTyra = (item) => {
  let achieved = item.tyra * 100 / item.tyraProjection;
  achieved = achieved > 100 ? 100 : parseFloat(achieved.toFixed(2));
  const toGoal = parseFloat((100 - achieved).toFixed(2));
  return [
    { name: 'Receita realizada', value: achieved, color: '#4CAF50' },
    { name: 'Necessário para a meta', value: toGoal, color: '#F44336' },
  ];
}

const getDataMarketStories = (item) => {
  let achieved = item.marketStories * 100 / item.marketStoriesProjection;
  achieved = achieved > 100 ? 100 : parseFloat(achieved.toFixed(2));
  const toGoal = parseFloat((100 - achieved).toFixed(2));
  return [
    { name: 'Receita realizada', value: achieved, color: '#4CAF50' },
    { name: 'Necessário para a meta', value: toGoal, color: '#F44336' },
  ];
}

const Panel = ({ month, year, total, setTotal, setProjectionValue }) => {
  const [data, setData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [dataSend, setDataSend] = useState([]);
  const [dataTyra, setDataTyra] = useState([]);
  const [dataMarketStories, setDataMarketStories] = useState([]);
  const [loading, setLoading] = useState(false);


  const fetchData = async (newMonth, newYear) => {
    reportRequests.getByPeriod(newMonth, newYear, newMonth, newYear).then(response => {
      const newData = response.data[0];
//      console.log('newData ', newData);
//      console.log('getRevenueData(newData) ', getRevenueData(newData));
      setData(newData);
      setRevenueData(getRevenueData(newData));
      setDataSend(getDataSend(newData));
      setDataTyra(getDataTyra(newData));
      setDataMarketStories(getDataMarketStories(newData));

      setTotal(newData.revenues);
      setProjectionValue(newData.revenuesProjection);

      setLoading(false);
    }).catch(err => {
      console.log(err);
      setData([]);
      setRevenueData([]);
      setDataSend([]);
      setDataTyra([]);
      setDataMarketStories([]);
      setTotal(null);
      setProjectionValue(null);
      setLoading(false);
    });
  }

  useEffect(() => {
    fetchData(month, year);
  }, [month, year]);



  return (
    <div style={{ padding: '20px 0' }}>
      <h3>Receitas por Categoria</h3>

      <div>
        <Table
          dataSource={revenueData}
          columns={revenueColumns}
          pagination={false}
          bordered
        />
      </div>

      <Row gutter={[16, 16]} style={{ paddingTop: 20 }}>
        <Col xs={24} md={8}>
          <MyPieChart title="Disparos" data={dataSend} show={!!data.sendsProjection} />
        </Col>
        <Col xs={24} md={8}>
          <MyPieChart title="Tyra" data={dataTyra} show={!!data.tyraProjection} />
        </Col>
        <Col xs={24} md={8}>
          <MyPieChart title="Marketstories" data={dataMarketStories} show={!!data.marketStoriesProjection} />
        </Col>
      </Row>

      <br />
      <br />
      <br />

      {/*
      <Card title="Painel de Metas e Lucro">
        <Table
          dataSource={panelData}
          columns={panelColumns}
          pagination={false}
          bordered
          rowClassName={(record) =>
            record.key === 'total' ? 'total-row' : ''
          }
        />
      </Card>
      <br />
      <br />
      <div style={{ maxWidth: '34%', textAlign: 'center', margin: '0 auto' }}>
        <MyBarChart />
      </div>
      */}
    </div>
  );
};

export default Panel;
